// ─── Chord Progression Suggestion Engine v2 ───────────────────────────────────
// Includes next-chord suggestions AND inversion recommendations

const TRANSITION_WEIGHTS = {
  Tonika:      { Tonika:2, Subdominante:8, Dominante:7, Supertonica:6, Mediante:5, Submediane:4, Leitton:2, Subtonica:2 },
  Subdominante:{ Tonika:4, Subdominante:2, Dominante:9, Supertonica:5, Mediante:3, Submediane:3, Leitton:6, Subtonica:3 },
  Dominante:   { Tonika:10,Subdominante:3, Dominante:1, Supertonica:2, Mediante:2, Submediane:6, Leitton:2, Subtonica:2 },
  Supertonica: { Tonika:2, Subdominante:5, Dominante:8, Supertonica:1, Mediante:3, Submediane:3, Leitton:5, Subtonica:2 },
  Mediante:    { Tonika:4, Subdominante:6, Dominante:5, Supertonica:4, Mediante:1, Submediane:5, Leitton:3, Subtonica:4 },
  Submediane:  { Tonika:3, Subdominante:7, Dominante:6, Supertonica:7, Mediante:3, Submediane:1, Leitton:4, Subtonica:3 },
  Leitton:     { Tonika:9, Subdominante:3, Dominante:4, Supertonica:2, Mediante:2, Submediane:2, Leitton:1, Subtonica:2 },
  Subtonica:   { Tonika:8, Subdominante:4, Dominante:5, Supertonica:3, Mediante:3, Submediane:3, Leitton:2, Subtonica:1 },
};

const NAMED_PATTERNS = [
  { name:"II–V–I",        pattern:["Supertonica","Dominante"],   next:"Tonika",       confidence:0.98 },
  { name:"I–IV–V",        pattern:["Tonika","Subdominante"],     next:"Dominante",    confidence:0.90 },
  { name:"I–V–VI",        pattern:["Tonika","Dominante"],        next:"Submediane",   confidence:0.75 },
  { name:"VI–IV–I–V",     pattern:["Submediane","Subdominante"], next:"Tonika",       confidence:0.85 },
  { name:"I–VI–II–V",     pattern:["Tonika","Submediane"],       next:"Supertonica",  confidence:0.80 },
  { name:"Andalusisch",   pattern:["Tonika","Leitton"],          next:"Submediane",   confidence:0.85 },
  { name:"Trugschluss",   pattern:["Subdominante","Dominante"],  next:"Submediane",   confidence:0.70 },
  { name:"Plagale Kadenz",pattern:["Dominante","Tonika"],        next:"Subdominante", confidence:0.60 },
];

// Voice-leading: get the bass note of a chord in a given inversion
// inv=0: root position, inv=1: first inversion (3rd in bass), inv=2: second inversion (5th in bass)
// inv=3: third inversion for 7th chords (7th in bass)
export function getInversionNotes(notes, inversion) {
  if (!notes || notes.length === 0) return notes;
  const inv = inversion % notes.length;
  return [...notes.slice(inv), ...notes.slice(0, inv)];
}

export function inversionName(inv, chordSize) {
  if (inv === 0) return "Grundstellung";
  if (inv === 1) return "1. Umkehrung";
  if (inv === 2) return "2. Umkehrung";
  if (inv === 3 && chordSize === 4) return "3. Umkehrung";
  return "Umkehrung";
}

export function inversionShortName(inv) {
  if (inv === 0) return "GS";
  if (inv === 1) return "1U";
  if (inv === 2) return "2U";
  if (inv === 3) return "3U";
  return "";
}

// Recommend best inversion for voice leading:
// Minimise the movement of the bass note from the previous chord's top/bass note
export function recommendInversion(prevNotes, nextNotes) {
  if (!prevNotes || prevNotes.length === 0) return { inversion: 0, reason: "Grundstellung als Einstieg" };

  const NOTE_SEMI = {'C':0,'C#':1,'Db':1,'D':2,'D#':3,'Eb':3,'E':4,'F':5,'F#':6,'Gb':6,'G':7,'G#':8,'Ab':8,'A':9,'A#':10,'Bb':10,'B':11};
  function semi(n) { return NOTE_SEMI[n] ?? 0; }

  // Previous chord's bass note
  const prevBass = semi(prevNotes[0]);
  // Previous chord's top note
  const prevTop  = semi(prevNotes[prevNotes.length - 1]);

  let bestInv = 0;
  let bestScore = Infinity;
  let bestReason = "Grundstellung";

  for (let inv = 0; inv < nextNotes.length; inv++) {
    const rotated = getInversionNotes(nextNotes, inv);
    const bass = semi(rotated[0]);

    // Score = distance between previous bass and this bass (prefer step-wise movement)
    let dist = Math.abs(bass - prevBass);
    if (dist > 6) dist = 12 - dist; // wrap around octave

    // Bonus: bass moves by a 4th or 5th (strong harmonic movement)
    if (dist === 5 || dist === 7) dist -= 1;
    // Penalty: bass stays on same note (no movement)
    if (dist === 0) dist += 0.5;

    if (dist < bestScore) {
      bestScore = dist;
      bestInv = inv;
      bestReason = inv === 0
        ? "Stufenweise Bassbewegung – ruhige Stimmführung"
        : inv === 1
          ? "Terz im Bass – leichter, fließender Übergang"
          : inv === 2
            ? "Quinte im Bass – instabil, drängt zur Auflösung"
            : "Septime im Bass – jazzy, chromatische Linie";
    }
  }

  return { inversion: bestInv, reason: bestReason };
}

export function getSuggestions(chords, allScaleChords, count = 4) {
  if (!chords || chords.length === 0) {
    const starters = ["Tonika","Subdominante","Submediane","Supertonica"];
    return allScaleChords
      .filter(c => starters.includes(c.fn))
      .map(c => ({ chord:c, reason:startReason(c.fn), confidence:startConf(c.fn), pattern:null, inversion:0, invReason:"Grundstellung als Einstieg" }))
      .slice(0, count);
  }

  const lastFn = chords[chords.length-1].fn;
  const weights = TRANSITION_WEIGHTS[lastFn] || {};

  // Named pattern matches
  const patternMatches = [];
  if (chords.length >= 2) {
    const last2 = [chords[chords.length-2].fn, chords[chords.length-1].fn];
    for (const pat of NAMED_PATTERNS) {
      if (pat.pattern[0]===last2[0] && pat.pattern[1]===last2[1]) {
        const match = allScaleChords.find(c => c.fn===pat.next);
        if (match) patternMatches.push({
          chord: match, reason:`Vervollständigt ${pat.name}`,
          confidence: pat.confidence, pattern: pat.name,
          inversion:0, invReason:"",
        });
      }
    }
  }

  const scored = allScaleChords.map(c => ({
    chord: c,
    score: (weights[c.fn]||1) + Math.random()*0.3,
    reason: transitionReason(lastFn, c.fn),
    confidence: Math.min(0.95, (weights[c.fn]||1)/10),
    pattern: null, inversion:0, invReason:"",
  }));
  scored.sort((a,b) => b.score - a.score);

  const seen = new Set(patternMatches.map(m => m.chord.degree));
  const regular = scored.filter(s => !seen.has(s.chord.degree)).slice(0, count - patternMatches.length);
  return [...patternMatches, ...regular].slice(0, count);
}

function startReason(fn) {
  return { Tonika:"Stabiler Start – Heimatgefühl", Subdominante:"Bewegung von Anfang an", Submediane:"Emotionaler, dunklerer Einstieg", Supertonica:"Jazzy – für II–V–I geeignet", Mediante:"Unerwarteter, farbiger Start", Dominante:"Direkt mit Spannung beginnen" }[fn] || "Guter Einstieg";
}
function startConf(fn) {
  return { Tonika:0.9, Subdominante:0.7, Submediane:0.75, Supertonica:0.65, Mediante:0.5, Dominante:0.6 }[fn] || 0.4;
}
function transitionReason(from, to) {
  const key = `${from}→${to}`;
  return ({
    "Tonika→Subdominante":"Klassische Öffnung: Heimat → Bewegung",
    "Tonika→Dominante":"Direkt zur Spannung",
    "Tonika→Supertonica":"II–V–I vorbereiten",
    "Tonika→Submediane":"Emotionaler Schwenk",
    "Subdominante→Dominante":"S–D: Spannung aufbauen",
    "Subdominante→Leitton":"Starke Hinführung zur Tonika",
    "Dominante→Tonika":"Perfekte Auflösung (V→I)",
    "Dominante→Submediane":"Trugschluss – überrascht das Ohr",
    "Supertonica→Dominante":"II–V: Jazz-Klassiker",
    "Submediane→Supertonica":"VI–II–V–I vorbereiten",
    "Submediane→Subdominante":"Weiche Bewegung in der Moll-Sphäre",
    "Leitton→Tonika":"Leitton löst zur Tonika auf",
    "Mediante→Subdominante":"Farbiger Übergang",
  })[key] || `${from} → ${to}`;
}
